// Combo chart (per-series chart type + optional secondary Y axis) - pure helpers shared by
// the chart editor, the workbook preview, and configurationValidation.
//
// Server-side mirrors (keep in step):
//   - CHART_TYPE_CATALOG      <-> ReportChartTypeCatalog.cs and the rc.ChartType seed rows
//   - validateComboChart      <-> ReportChartConfigurationValidator.Validate
//
// A combo "series" is one pivot ROW of the block - a breakdown value, the total row, or a
// derived row such as a ratio - identified by its row label, the same unit the single-type
// chart's seriesRowLabels selects. Only listed rows are plotted in combo mode.
import type {
    ExcelChartAxisKey,
    ExcelChartAxisSettings,
    ExcelChartLayout,
    ExcelChartType,
    ExcelComboChartSettings,
    ExcelComboSeriesSettings,
    ExcelComboSeriesType,
    MetricSection,
    ReportConfig,
    SectionExcelLayout,
} from "../types";
import { combinedRowLabel, combinedRows, usesCombinedRuleSets } from "./combinedRuleSetSelection";

// Labels of a combined table's visible rows in sheet order (same order rule as
// workbookLayout.combinedSheetRows, kept local to avoid an import cycle).
function combinedSheetRowLabels(section: MetricSection, layout: SectionExcelLayout | null | undefined) {
    const rows = combinedRows(section);
    const order = layout?.combinedRowOrder || [];
    const ordered = order.map((id) => rows.find((row) => row.id === id)).filter(Boolean) as typeof rows;
    return ordered.concat(rows.filter((row) => !ordered.includes(row)))
        .filter((row) => !layout?.combinedRowOptions?.[row.id]?.hidden)
        .map(combinedRowLabel);
}

export interface ChartTypeDescriptor {
    key: ExcelChartType | ExcelComboSeriesType;
    label: string;
    isCartesian: boolean;
    allowedStandalone: boolean;
    allowedInCombo: boolean;
}

// Keys match rc.ChartType.ChartTypeKey. "none" is not a chart type; it is "no chart".
export const CHART_TYPE_CATALOG: ChartTypeDescriptor[] = [
    { key: "clusteredColumn", label: "Clustered column", isCartesian: true, allowedStandalone: true, allowedInCombo: true },
    { key: "stackedColumn", label: "Stacked column", isCartesian: true, allowedStandalone: true, allowedInCombo: false },
    { key: "line", label: "Line", isCartesian: true, allowedStandalone: true, allowedInCombo: true },
];

export const COMBO_SERIES_TYPE_OPTIONS: { value: ExcelComboSeriesType; label: string }[] = [
    { value: "clusteredColumn", label: "Column" },
    { value: "line", label: "Line" },
];

export const COMBO_AXIS_OPTIONS: { value: ExcelChartAxisKey; label: string }[] = [
    { value: "primary", label: "Primary (left)" },
    { value: "secondary", label: "Secondary (right)" },
];

export const COMBO_MIN_SERIES = 2;

const COMBO_REJECTION_REASONS: Record<string, string> = {
    pie: "Pie charts have no value axis, so they can't be combined with other series.",
    donut: "Donut charts have no value axis, so they can't be combined with other series.",
    doughnut: "Donut charts have no value axis, so they can't be combined with other series.",
    bar: "Horizontal bar series can't be mixed with vertical-axis series. Use a column series instead.",
    stackedBar: "Horizontal bar series can't be mixed with vertical-axis series. Use a column series instead.",
    stackedColumn: "Stacked columns aren't supported inside a combo chart yet. Use a clustered column series instead.",
};

const HEX_COLOR = /^[0-9A-Fa-f]{6}$/;

export function isComboAllowedType(key: string | null | undefined): key is ExcelComboSeriesType {
    return CHART_TYPE_CATALOG.some((type) => type.key === key && type.allowedInCombo);
}

export function comboRejectionReason(key: string | null | undefined): string | null {
    if (isComboAllowedType(key)) return null;
    const trimmed = String(key ?? "").trim();
    return COMBO_REJECTION_REASONS[trimmed] || `Chart type "${trimmed}" can't be used in a combo chart. Choose Column or Line.`;
}

export function isComboChart(chart: ExcelChartLayout | null | undefined): boolean {
    return chart?.mode === "combo";
}

export function makeDefaultChartAxisSettings(): ExcelChartAxisSettings {
    return { title: "", numberFormat: "general", minimum: null, maximum: null };
}

export function makeDefaultComboChartSettings(): ExcelComboChartSettings {
    return { series: [], secondaryAxis: makeDefaultChartAxisSettings() };
}

export function makeComboSeries(seriesKey: string, displayOrder: number, chartType: ExcelComboSeriesType = "clusteredColumn", axis: ExcelChartAxisKey = "primary"): ExcelComboSeriesSettings {
    return { seriesKey, chartType, axis, displayOrder, color: null, label: "", showDataLabels: null };
}

function optionalFiniteNumber(value: unknown): number | null {
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}

// Tolerant read of whatever ConfigJson / a template carried. Unknown series types are kept
// as-is (typed loosely) so validation can name them instead of silently rewriting them.
export function normalizeComboChartSettings(raw: Partial<ExcelComboChartSettings> | null | undefined): ExcelComboChartSettings {
    const series = Array.isArray(raw?.series) ? raw!.series : [];
    const axis: Partial<ExcelChartAxisSettings> = raw?.secondaryAxis || {};
    return {
        series: series
            .filter((item) => item && typeof item === "object")
            .map((item, index) => ({
                seriesKey: String(item.seriesKey ?? ""),
                chartType: (item.chartType || "clusteredColumn") as ExcelComboSeriesType,
                axis: item.axis === "secondary" ? "secondary" : "primary",
                displayOrder: Number.isFinite(item.displayOrder) ? Number(item.displayOrder) : index,
                color: typeof item.color === "string" && item.color.trim() ? item.color.trim().replace(/^#/, "") : null,
                label: typeof item.label === "string" ? item.label : "",
                showDataLabels: typeof item.showDataLabels === "boolean" ? item.showDataLabels : null,
            })),
        secondaryAxis: {
            title: typeof axis.title === "string" ? axis.title : "",
            numberFormat: axis.numberFormat || "general",
            minimum: optionalFiniteNumber(axis.minimum),
            maximum: optionalFiniteNumber(axis.maximum),
        },
    };
}

export function orderedComboSeries(combo: ExcelComboChartSettings | null | undefined): ExcelComboSeriesSettings[] {
    return (combo?.series || [])
        .map((series, index) => ({ series, index }))
        .sort((a, b) => a.series.displayOrder - b.series.displayOrder || a.index - b.index)
        .map((item) => item.series);
}

// Rewrites displayOrder as 0..n-1 in the given order, so reordering never leaves gaps/ties.
export function renumberComboSeries(series: ExcelComboSeriesSettings[]): ExcelComboSeriesSettings[] {
    return series.map((item, index) => ({ ...item, displayOrder: index }));
}

export function moveComboSeries(combo: ExcelComboChartSettings, seriesIndex: number, direction: -1 | 1): ExcelComboChartSettings {
    const ordered = orderedComboSeries(combo);
    const target = seriesIndex + direction;
    if (target < 0 || target >= ordered.length) return combo;
    const next = [...ordered];
    [next[seriesIndex], next[target]] = [next[target], next[seriesIndex]];
    return { ...combo, series: renumberComboSeries(next) };
}

// Mirrors ReportChartConfigurationValidator.Validate. Returns [] when combo is off or the
// chart is disabled - a draft keeps its series table without being blocked by it.
export function validateComboChart(chart: ExcelChartLayout | null | undefined): string[] {
    const messages: string[] = [];
    if (!chart) return messages;
    if (chart.mode && chart.mode !== "single" && chart.mode !== "combo") {
        messages.push(`Chart mode "${chart.mode}" is not supported. Use single or combo.`);
        return messages;
    }
    if (!chart.enabled || !isComboChart(chart)) return messages;

    if (chart.dataOrientation === "rowsAsCategories") {
        messages.push('A combo chart plots table rows as series. Set the bottom axis categories to "Table columns".');
    }

    const series = chart.combo?.series || [];
    if (series.length < COMBO_MIN_SERIES) {
        messages.push(`A combo chart needs at least ${COMBO_MIN_SERIES} series. Add another row to the series table, or switch combo mode off.`);
    }

    const seen = new Set<string>();
    let primaryCount = 0;
    series.forEach((item) => {
        const key = String(item?.seriesKey ?? "").trim();
        if (!key) {
            messages.push("Every combo series needs a row label.");
            return;
        }
        const normalized = key.toLowerCase();
        if (seen.has(normalized)) messages.push(`"${key}" is listed more than once in the series table.`);
        seen.add(normalized);

        const rejection = comboRejectionReason(item.chartType);
        if (rejection) messages.push(`Series "${key}": ${rejection}`);

        if (item.axis === "primary") primaryCount += 1;
        else if (item.axis !== "secondary") messages.push(`Series "${key}" has an unknown axis "${item.axis}". Use primary or secondary.`);

        if (item.color && !HEX_COLOR.test(String(item.color).trim())) {
            messages.push(`Series "${key}" has an invalid color. Use a six-digit hex value such as 1F4E78.`);
        }
    });

    if (series.length > 0 && primaryCount === 0) messages.push("At least one combo series must use the primary axis.");

    const axis = chart.combo?.secondaryAxis;
    if (axis?.minimum != null && axis?.maximum != null && axis.minimum >= axis.maximum) {
        messages.push("The secondary axis minimum must be less than its maximum.");
    }

    return messages;
}

function pushUnique(target: string[], seen: Set<string>, value: string | null | undefined) {
    const trimmed = String(value ?? "").trim();
    if (!trimmed) return;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    target.push(trimmed);
}

// Row labels the block is expected to produce, offered as suggestions in the series table.
// Breakdown values are only known for sure once the query runs, so this is a best effort from
// the configuration (groups, include list, row-order/label settings, rule-set categories,
// the total row, derived rows); the input still accepts any label.
export function comboSeriesSuggestions(section: MetricSection, layout: SectionExcelLayout | null | undefined, report: ReportConfig | null | undefined): string[] {
    const suggestions: string[] = [];
    const seen = new Set<string>();
    const rowLabelColumn = layout?.columns.find((column) => column.role === "rowLabel");
    const labelMap = rowLabelColumn?.rowLabel?.labelMap || {};
    const mapped = (value: string) => labelMap[value] || value;

    // A combined table's series are its own rows (logic/combinedRuleSets.ts).
    if (usesCombinedRuleSets(section)) {
        combinedSheetRowLabels(section, layout).forEach((label) => pushUnique(suggestions, seen, label));
        (layout?.derivedRows || []).forEach((row) => pushUnique(suggestions, seen, row.label));
        return suggestions;
    }
    (section.groups || []).forEach((group) => pushUnique(suggestions, seen, group.name));
    if (!section.groups?.length) (section.include || []).forEach((value) => pushUnique(suggestions, seen, mapped(value)));
    (rowLabelColumn?.rowLabel?.explicitOrder || []).forEach((value) => pushUnique(suggestions, seen, mapped(value)));
    Object.values(labelMap).forEach((value) => pushUnique(suggestions, seen, value));

    if (section.breakdownSource === "ruleSet" && report) {
        const ruleSet = report.bucketRuleSets.find((set) => set.id === section.ruleSetId);
        ruleSet?.rules.forEach((rule) => {
            if (rule.action !== "set") return;
            // Typed values only: a column-sourced rule ("Set value from a column") has no
            // fixed category to suggest - its categories are whatever the column holds.
            if (rule.valueSource === "column") return;
            pushUnique(suggestions, seen, (rule.valueCustom || "").trim() || rule.valueFromList || "");
        });
        if (ruleSet?.includeElse) pushUnique(suggestions, seen, ruleSet.elseLabel || "Everything Else");
    }

    if (rowLabelColumn?.rowLabel?.showTotalRow && rowLabelColumn.rowLabel.totalPosition !== "none") {
        pushUnique(suggestions, seen, rowLabelColumn.rowLabel.totalLabel || "Total");
    }
    (layout?.derivedRows || []).forEach((row) => pushUnique(suggestions, seen, row.label));
    return suggestions;
}

// Seeds the series table the first time combo is switched on: first suggested row as a
// column, and a ratio derived row (if any) - else the second suggestion - as a line on the
// secondary axis. Returns [] when there is nothing to suggest; the user then adds rows.
export function seedComboSeries(suggestions: string[], layout: SectionExcelLayout | null | undefined): ExcelComboSeriesSettings[] {
    const ratioRow = (layout?.derivedRows || []).find((row) => row.kind === "ratio" && row.label.trim());
    const first = suggestions.find((label) => !ratioRow || label.toLowerCase() !== ratioRow.label.trim().toLowerCase());
    const series: ExcelComboSeriesSettings[] = [];
    if (first) series.push(makeComboSeries(first, 0, "clusteredColumn", "primary"));
    if (ratioRow) {
        series.push(makeComboSeries(ratioRow.label.trim(), series.length, "line", "secondary"));
    } else {
        const second = suggestions.find((label) => label !== first);
        if (second) series.push(makeComboSeries(second, series.length, "line", "primary"));
    }
    return series;
}

export function comboHasLineSeries(chart: ExcelChartLayout | null | undefined): boolean {
    return isComboChart(chart) && (chart?.combo?.series || []).some((series) => series.chartType === "line");
}

export function comboHasColumnSeries(chart: ExcelChartLayout | null | undefined): boolean {
    return isComboChart(chart) && (chart?.combo?.series || []).some((series) => series.chartType !== "line");
}

export function comboUsesSecondaryAxis(chart: ExcelChartLayout | null | undefined): boolean {
    const series = chart?.combo?.series || [];
    return isComboChart(chart) && series.some((item) => item.axis === "secondary") && series.some((item) => item.axis === "primary");
}

// Preview-only mirror of ExcelReportRenderer.ChartPalettes. "office" is Excel's own default
// series sequence (the renderer writes no colour for it and Excel applies these).
export const CHART_PALETTE_COLORS: Record<string, string[]> = {
    office: ["4472C4", "ED7D31", "A5A5A5", "FFC000", "5B9BD5", "70AD47"],
    blue: ["1F4E78", "5B9BD5", "9DC3E6", "2F75B5", "BDD7EE", "17365D"],
    colorful: ["4472C4", "ED7D31", "A5A5A5", "FFC000", "5B9BD5", "70AD47"],
    warm: ["C00000", "ED7D31", "FFC000", "A64D79", "E06666", "F6B26B"],
    cool: ["2F75B5", "00A6A6", "70AD47", "8064A2", "5B9BD5", "76A5AF"],
    monochrome: ["1F2937", "4B5563", "6B7280", "9CA3AF", "D1D5DB", "E5E7EB"],
};

// The colour a combo series will get: its own override, else its palette slot by position.
export function comboSeriesColor(series: ExcelComboSeriesSettings, position: number, palette: string | undefined): string {
    const custom = String(series.color ?? "").trim().replace(/^#/, "");
    if (HEX_COLOR.test(custom)) return custom.toUpperCase();
    const colors = CHART_PALETTE_COLORS[palette || "office"] || CHART_PALETTE_COLORS.office;
    return colors[position % colors.length];
}
